import { useState } from "react";
import { ExternalLink, MousePointerClick, ArrowUpRight, Flame, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { money, timeAgo, initials } from "@/lib/format";
import { trackClick } from "@/lib/api";

export const LeaderboardRow = ({ row, featured = false, hot = false, onClaim, onRaise }) => {
    const [clicking, setClicking] = useState(false);

    const openTarget = async () => {
        setClicking(true);
        try {
            const res = await trackClick(row.id);
            const target = res?.target || (row.type === "handle"
                ? `https://x.com/${(row.value || "").replace(/^@/, "")}`
                : row.value);
            window.open(target, "_blank", "noopener,noreferrer");
        } catch (e) {
            const target = row.type === "handle"
                ? `https://x.com/${(row.value || "").replace(/^@/, "")}`
                : row.value;
            window.open(target, "_blank", "noopener,noreferrer");
        } finally {
            setClicking(false);
        }
    };

    if (featured) {
        return (
            <div
                data-testid={`leaderboard-featured-${row.id}`}
                className="relative overflow-hidden rounded-2xl border border-brand/40 bg-card p-5 sm:p-6 glow-brand"
            >
                <div className="absolute inset-0 hero-wash opacity-70 pointer-events-none" />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="grid place-items-center h-14 w-14 rounded-xl bg-brand text-primary-foreground font-heading text-2xl font-bold shrink-0">
                            1
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    data-testid={`leaderboard-name-${row.id}`}
                                    onClick={openTarget}
                                    disabled={clicking}
                                    className="font-heading text-xl sm:text-2xl font-bold truncate hover:text-brand transition-colors inline-flex items-center gap-1.5"
                                >
                                    {row.display_name}
                                    <ExternalLink className="h-4 w-4 opacity-60" />
                                </button>
                                <Badge className="bg-brand/20 text-brand border-brand/30 hover:bg-brand/20">
                                    <TrendingUp className="h-3 w-3 mr-1" /> TOP SPOT
                                </Badge>
                            </div>
                            {row.specialty ? (
                                <Badge variant="secondary" className="mt-1.5 mr-2">{row.specialty}</Badge>
                            ) : null}
                            {row.description ? (
                                <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{row.description}</p>
                            ) : null}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1 tabular"><MousePointerClick className="h-3.5 w-3.5" /> {row.clicks} clicks</span>
                                <span>· {timeAgo(row.created_at)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex sm:flex-col items-end justify-between gap-3 shrink-0">
                        <div className="text-right">
                            <div className="font-heading text-3xl font-bold tabular text-brand">{money(row.bid_amount)}</div>
                            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">current bid</div>
                        </div>
                        <Button
                            data-testid={`leaderboard-claim-button-${row.id}`}
                            onClick={() => onClaim(row)}
                            className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                        >
                            Own #1 for {money(row.claim_amount)} <ArrowUpRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </div>
                <div className="relative mt-4 pt-3 border-t border-border flex justify-end">
                    <button
                        data-testid={`leaderboard-raise-button-${row.id}`}
                        onClick={() => onRaise(row)}
                        className="text-xs text-muted-foreground hover:text-heat transition-colors inline-flex items-center gap-1"
                    >
                        Raise this listing&apos;s bid
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid={`leaderboard-row-${row.id}`}
            className={`group flex items-center gap-3 rounded-xl border bg-card px-3 py-3 sm:px-4 transition-colors ${
                hot
                    ? "border-heat/40 ring-1 ring-heat/20"
                    : "border-border hover:border-white/15 hover:bg-secondary/40"
            }`}
        >
            <div className="grid place-items-center min-w-10 h-10 rounded-lg bg-secondary text-sm font-semibold tabular text-muted-foreground shrink-0">
                {row.rank}
            </div>

            <div className="grid place-items-center h-9 w-9 rounded-lg bg-surface-2 text-xs font-bold text-muted-foreground shrink-0">
                {initials(row.display_name)}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`leaderboard-name-${row.id}`}
                        onClick={openTarget}
                        disabled={clicking}
                        className="font-medium truncate hover:text-brand transition-colors inline-flex items-center gap-1"
                    >
                        {row.display_name}
                        <ExternalLink className="h-3.5 w-3.5 opacity-50" />
                    </button>
                    {hot ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-heat">
                            <Flame className="h-3 w-3" /> HOT
                        </span>
                    ) : null}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    {row.specialty ? (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 truncate max-w-[140px]">{row.specialty}</Badge>
                    ) : null}
                    <span className="text-xs text-muted-foreground tabular inline-flex items-center gap-1">
                        <MousePointerClick className="h-3 w-3" /> {row.clicks}
                    </span>
                    <span className="text-xs text-muted-foreground">· {timeAgo(row.created_at)}</span>
                </div>
            </div>

            <div className="text-right shrink-0">
                <div className="font-heading font-semibold tabular">{money(row.bid_amount)}</div>
                <button
                    data-testid={`leaderboard-raise-button-${row.id}`}
                    onClick={() => onRaise(row)}
                    className="text-[11px] text-muted-foreground hover:text-heat transition-colors"
                >
                    raise bid
                </button>
            </div>

            <Button
                data-testid={`leaderboard-claim-button-${row.id}`}
                size="sm"
                variant="outline"
                onClick={() => onClaim(row)}
                className="shrink-0 border-brand/40 text-brand hover:bg-brand hover:text-primary-foreground font-semibold"
            >
                Claim {money(row.claim_amount)}
            </Button>
        </div>
    );
};
